import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import Joi from 'joi';

import { useCreateQuestionMutation, useUpdateQuestionMutation } from 'api/main';
import {
    editingAnswerIdSet,
    editingQuestionAnswerRemoved,
    editingQuestionAnswerUpdated,
    editingQuestionIdSet,
    editingQuestionSet,
    newEditingQuestionIdSet,
    newStepQuestionUpdated,
} from 'reducers/customTunnel';
import { customTunnelSelectors } from 'reducers';
import { customTunnelSchemas } from 'schemas';
import { useDialog } from 'utils/hooks';
import { validateSchema } from 'utils/helpers';
import { questionType } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import AnswerItem from './AnswerItem';
import {
    ActionButton,
    ConfirmDialog,
    CustomFormLabel,
    CustomTextField,
    Loader,
    RadioLabel,
} from 'components';
import { Box, RadioGroup } from '@mui/material';
import * as icons from 'mdi-material-ui';
import { Droppable } from 'react-beautiful-dnd';

const radioGroupSx = { marginBottom: 2 };
const schema = {
    title: Joi.string().required().trim().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
};

const QuestionForm = ({
    onRemove,
    question, // pp tunnel question object
    pmQuestions,
    questionIndex,
}) => {
    const dispatch = useDispatch();
    const form = useSelector(customTunnelSelectors.getEditingQuestion);
    const editingAnswerId = useSelector(customTunnelSelectors.getEditingAnswerId);

    const {
        open: confirmSaveOpen,
        onOpen: handleConfirmOpen,
        onClose: handleConfirmClose,
    } = useDialog();

    const [errors, setErrors] = useState({ title: '' });
    const { ppId: editingPPTunnelStepId, pmId: editingPMTunnelStepId } = useSelector(
        customTunnelSelectors.getEditingStepId
    );

    const [
        updateQuestion,
        { isLoading: updateLoading, isSuccess: updateSuccess, reset: updateReset },
    ] = useUpdateQuestionMutation();
    const [
        createQuestion,
        { isLoading: createLoading, isSuccess: createSuccess, reset: createReset },
    ] = useCreateQuestionMutation();

    const handleChange = useCallback(
        key => e => {
            setErrors({ title: '' });
            dispatch(editingQuestionSet({ key, value: e.target.value }));
            if (key === 'type' && e.target.value === questionType.TEXT) {
                dispatch(editingQuestionSet({ key: 'answers', value: [] }));
            }
        },
        [dispatch]
    );

    const handleAddAnswer = useCallback(() => {
        dispatch(
            editingQuestionSet({
                key: 'answers',
                value: [...form.answers, { title: '', score: '' }],
            })
        );
    }, [dispatch, form?.answers]);

    const handleRemoveAnswer = useCallback(
        index => () => {
            dispatch(editingQuestionAnswerRemoved({ answerIndex: index }));
        },
        [dispatch]
    );

    const handleUpdateAnswer = useCallback(
        index => newAnswer => {
            dispatch(editingQuestionAnswerUpdated({ answerIndex: index, value: newAnswer }));
        },
        [dispatch]
    );

    const handleClearEmptyAnswers = useCallback(() => {
        if (form?.answers?.length) {
            dispatch(
                editingQuestionSet({
                    key: 'answers',
                    value: form.answers.filter(a => a.title),
                })
            );
        }
    }, [dispatch, form?.answers]);

    const handleCancel = useCallback(() => {
        if (!form.title || !question?.title) {
            // empty question will be removed
            onRemove();
        }
        // clear editing Ids
        dispatch(editingQuestionIdSet(null));
        dispatch(newEditingQuestionIdSet(null));
        // clear editing answer id
        dispatch(editingAnswerIdSet(null));
        // clear question form in store
        dispatch(editingQuestionSet(null));
    }, [question, onRemove, dispatch, form?.title]);

    const handleSave = useCallback(() => {
        if (question?.id) {
            // question exists in the DB
            updateQuestion(
                customTunnelSchemas.updateQuestion({
                    form,
                    ppQuestionId: question?.id,
                    pmQuestionId: pmQuestions[questionIndex]?.id,
                })
            );
        } else if (editingPPTunnelStepId) {
            // tunnel step exists, we create the question in the DB
            createQuestion(
                customTunnelSchemas.createQuestion({
                    form,
                    ppTunnelStepId: editingPPTunnelStepId,
                    pmTunnelStepId: editingPMTunnelStepId,
                    questionIndex,
                })
            );
        } else {
            // question and step only exist in memory
            // update the question from store
            dispatch(newEditingQuestionIdSet(null));
            dispatch(
                newStepQuestionUpdated({
                    questionIndex,
                    value: { ...form, answers: form.answers.filter(a => a.title) },
                })
            );
            dispatch(editingQuestionSet(null));
        }
    }, [
        createQuestion,
        dispatch,
        editingPMTunnelStepId,
        editingPPTunnelStepId,
        form,
        pmQuestions,
        question?.id,
        questionIndex,
        updateQuestion,
    ]);

    const handleConfirmSave = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setErrors({ title: '' });
            if (editingAnswerId) {
                handleConfirmOpen();
            } else {
                handleSave();
            }
        } else {
            setErrors(newErrors);
        }
    }, [form, editingAnswerId, handleConfirmOpen, handleSave]);

    useEffect(() => {
        dispatch(editingQuestionSet({ value: question }));
    }, [dispatch, question]);

    useEffect(() => {
        if (createSuccess) {
            dispatch(newEditingQuestionIdSet(null));
            dispatch(editingQuestionSet(null));
            createReset();
        }
    }, [createReset, createSuccess, dispatch]);

    useEffect(() => {
        if (updateSuccess) {
            dispatch(editingQuestionIdSet(null));
            dispatch(editingQuestionSet(null));
            updateReset();
        }
    }, [dispatch, updateReset, updateSuccess]);

    return (
        <Box padding={1} width="100%">
            <CustomTextField
                label={texts.QUESTION_TEXT}
                onChange={handleChange('title')}
                value={form.title}
                error={errors.title}
            />
            <CustomFormLabel>{texts.QUESTION_COMPULSORINESS_LABEL}</CustomFormLabel>
            <RadioGroup
                value={form.mandatory}
                onChange={handleChange('mandatory')}
                sx={radioGroupSx}
            >
                <RadioLabel value={true} label={texts.MANDATORY} />
                <RadioLabel value={false} label={texts.OPTIONAL} />
            </RadioGroup>
            <CustomFormLabel>{texts.TYPE}</CustomFormLabel>
            <RadioGroup value={form.type} onChange={handleChange('type')} sx={radioGroupSx}>
                <RadioLabel value={questionType.TEXT} label={texts.TEXT_FIELDS} />
                <RadioLabel value={questionType.CHECKBOX} label={texts.CHECKBOXES} />
                <RadioLabel value={questionType.RADIO} label={texts.RADIO_BUTTONS} />
            </RadioGroup>
            <Box textAlign="center" mb={1}>
                <ActionButton
                    disabled={
                        (form.type === questionType.TEXT && form.answers.length === 1) ||
                        !form.answers.every(a => a.title) ||
                        !!editingAnswerId
                    }
                    variant="outlined"
                    sx={{ padding: '5px 8px' }}
                    onClick={handleAddAnswer}
                >
                    {texts.ADD_ANSWER}
                </ActionButton>
            </Box>
            <Droppable
                droppableId={`droppable-answers-${questionIndex}`}
                type={`answer-${questionIndex}`}
            >
                {(provided, snapshot) => (
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                        padding={3}
                        {...provided.droppableProps}
                        sx={{ opacity: snapshot.isDraggingOver ? '.85' : 1 }}
                        ref={provided.innerRef}
                    >
                        {form.answers.map((answer, idx) => (
                            <AnswerItem
                                key={`${answer.title}-${idx}`}
                                index={idx}
                                answer={answer}
                                answers={form.answers}
                                onRemove={handleRemoveAnswer(idx)}
                                onUpdate={handleUpdateAnswer(idx)}
                                pmQuestions={pmQuestions}
                                questionIndex={questionIndex}
                                formClear={handleClearEmptyAnswers}
                            />
                        ))}
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <ConfirmDialog
                    open={confirmSaveOpen}
                    handleClose={handleConfirmClose}
                    handleConfirm={handleSave}
                    title={texts.CHANGES_NOT_CONFIRMED}
                    confirmText={texts.CONFIRMER}
                    cancelText={texts.CANCEL}
                >
                    {texts.CONFIRM_DISCARD_CHANGES}
                    QF
                </ConfirmDialog>
                <icons.CloseThick
                    sx={{
                        cursor: 'pointer',
                        '&:hover': { color: 'error.main' },
                        pointerEvents: updateLoading || createLoading ? 'none' : 'unset',
                    }}
                    onClick={handleCancel}
                />
                <Loader loading={updateLoading || createLoading} marginTop={0} size={20}>
                    <icons.CheckBold
                        sx={{ cursor: 'pointer', '&:hover': { color: 'success.main' } }}
                        onClick={handleConfirmSave}
                    />
                </Loader>
            </Box>
        </Box>
    );
};

QuestionForm.propTypes = {
    question: PropTypes.object,
    onRemove: PropTypes.func.isRequired,
    pmQuestions: PropTypes.array,
    questionIndex: PropTypes.number,
};

export default QuestionForm;
